/*
The flagship panel on an industry page used to draw a stylised route sheet — a
handful of invented stops on no particular map. Daniel, on the Denver card:

  "this does a bit of a disservice to the example, because in reality there are
   way more locations than what is shown in the animation ... for specific
   examples a real map or image of the real map would be more powerful"

He is right, and the real map already exists. Every published example is
rendered at seed time to a Mapbox light-v11 static image — the same style the
app's own map uses — with every stop as a teal marker and every depot as an
orange warehouse pin. Denver's carries all 169 block faces and its 8 transfer
stations; the renderer only starts thinning above ~250 points.

So this shows `banner_image` from the example catalog, and falls back to the
canvas when there is no banner, no card, or the image fails to load.

Two deliberate choices:

- **The stylised canvas stays as the fallback, not as a spinner.** The catalog
  is an async fetch on a marketing page; a visitor on a slow connection should
  see the illustration rather than a hole, and it is a legitimate thing to look
  at rather than a placeholder.
- **`onError` falls back too.** An <img> whose URL resolves to something that is
  not an image renders as broken alt text, which is exactly how the footer
  wordmark failed in prod (a .webp against Amplify's rewrite allowlist). The
  banner URLs are content-hashed and change whenever an example is re-rendered,
  so a stale or missing object is a real possibility rather than a hypothetical.

The banner is 2:1. That is stated as an explicit aspect ratio so the panel does
not reflow when the image lands.
*/
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";

import { useEnsureExampleCatalog, flattenSummary } from "store/functions/exampleCatalog.js";
import { selectExampleBySlug } from "store/reducers/uiReducer.js";
import RouteSheetCanvas from "./RouteSheetCanvas.jsx";

/* "Map of 176 locations across Denver metro." — describes what is actually on
 * the map rather than repeating the headline, since a screen-reader user gets
 * the headline from the surrounding copy anyway. `flattenSummary` returns
 * [{label, value}], not an object. */
export function mapAltText(card, area) {
  const counts = flattenSummary(card?.summary)
    .filter((s) => /location|trip|stop/i.test(s.label))
    .map((s) => `${s.value} ${String(s.label).toLowerCase()}`);
  const what = counts.length ? counts.join(" and ") : "the example's stops";
  return `Map of ${what}${area ? ` across ${area}` : ""}.`;
}

/* The whole decision, kept out of the component so it is testable without a DOM
 * renderer (this repo has no component-rendering test library by choice). */
export function pickBanner(card, failed) {
  if (failed || !card) return null;
  return card.banner_image || null;
}

export default function FlagshipExampleMap({ slug, canvas, label, area }) {
  useEnsureExampleCatalog();
  const card = useSelector(selectExampleBySlug(slug || ""));
  const [failed, setFailed] = useState(false);

  const banner = pickBanner(card, failed);
  if (!banner) return <RouteSheetCanvas {...canvas} aspect="1 / 1" label={label} />;

  return (
    <Box
      component="img"
      src={banner}
      alt={mapAltText(card, area)}
      loading="lazy"
      onError={() => setFailed(true)}
      sx={{
        display: "block",
        width: "100%",
        aspectRatio: "2 / 1",
        objectFit: "cover",
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
      }}
    />
  );
}
